import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Sync as SyncIcon,
} from '@mui/icons-material';
import { RoadmapEditor } from '../../features/PMMode/components/Roadmap';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

export default function OutcomeRoadmapTab({ outcomeId, outcomeName, readOnly }) {
  const [roadmap, setRoadmap] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  useEffect(() => {
    if (!outcomeId) { setLoading(false); return; }
    setLoading(true);
    fetch(`${API_BASE}/api/roadmaps/?outcome_id=${outcomeId}`)
      .then((r) => r.ok ? r.json() : [])
      .then(async (list) => {
        if (list.length > 0) {
          // Fetch full roadmap (list endpoint returns summary only, without nodes/phases/releases)
          const fullRes = await fetch(`${API_BASE}/api/roadmaps/${list[0].id}`);
          const fullData = fullRes.ok ? await fullRes.json() : list[0];
          setRoadmap(fullData);
          setEnabled(true);
        }
      })
      .catch(() => setError('Could not check roadmap status.'))
      .finally(() => setLoading(false));
  }, [outcomeId]);

  // Stay in sync when the roadmap is saved from PM Mode or another context
  useEffect(() => {
    const handler = (e) => {
      const updated = e.detail?.roadmap;
      if (updated && roadmap?.id === updated.id) {
        setRoadmap(updated);
      }
    };
    window.addEventListener('roadmap-updated', handler);
    return () => window.removeEventListener('roadmap-updated', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roadmap?.id]);

  const handleToggleEnable = useCallback(async (checked) => {
    if (checked) {
      setSyncing(true);
      setError('');
      try {
        const res = await fetch(`${API_BASE}/api/roadmaps/from-outcome/${outcomeId}`, { method: 'POST' });
        if (!res.ok) throw new Error(`Failed to create roadmap (${res.status})`);
        const data = await res.json();
        setRoadmap(data);
        setEnabled(true);
      } catch (err) {
        setError(err.message);
      } finally {
        setSyncing(false);
      }
    } else {
      setConfirmDeleteOpen(true);
    }
  }, [outcomeId]);

  const handleConfirmDelete = useCallback(async () => {
    setConfirmDeleteOpen(false);
    if (!roadmap) return;
    setSyncing(true);
    try {
      const res = await fetch(`${API_BASE}/api/roadmaps/${roadmap.id}`, { method: 'DELETE' });
      if (!res.ok && res.status !== 204) throw new Error('Delete failed');
      setRoadmap(null);
      setEnabled(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  }, [roadmap]);

  const handleSync = useCallback(async () => {
    if (!roadmap) return;
    setSyncing(true);
    setError('');
    try {
      const res = await fetch(
        `${API_BASE}/api/roadmaps/from-outcome/${outcomeId}?force=true`,
        { method: 'POST' }
      );
      if (!res.ok) throw new Error('Sync failed');
      setRoadmap(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  }, [outcomeId, roadmap]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => handleToggleEnable(e.target.checked)}
              disabled={readOnly || syncing}
            />
          }
          label="Generate Roadmap from Milestones"
        />
        {syncing && <CircularProgress size={18} />}
      </Box>

      {!enabled && (
        <Typography variant="body2" color="text.secondary">
          Toggle on to auto-create a Gantt roadmap in PM Mode from this outcome's milestones.
        </Typography>
      )}

      {roadmap && (
        <>
          <Divider />
          {!readOnly && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title="Re-sync roadmap nodes from current milestones. Overwrites milestone names and dates but preserves status and dependencies.">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={syncing ? <CircularProgress size={14} /> : <SyncIcon />}
                    onClick={handleSync}
                    disabled={syncing}
                  >
                    Sync from Milestones
                  </Button>
                </span>
              </Tooltip>
            </Box>
          )}
          <RoadmapEditor
            roadmap={roadmap}
            isReadOnly={readOnly}
            onBack={null}
            onSaved={(saved) => setRoadmap(saved)}
            onDeleted={() => {}}
            embedded
          />
        </>
      )}

      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Delete Roadmap?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently delete the roadmap "{roadmap?.name}". This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button color="error" onClick={handleConfirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
